import { Component, OnInit, afterNextRender } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { SegurancaService, SigepeUserData } from '@sigepe/ngx-sigepe-seguranca';
import { FeriasBaseComponent } from '../ferias-base.component';
import { NivelAcessoUtil } from 'src/app/utils/nivel-acesso.util';
import { CpfPipe } from 'src/app/components/shared/pipes/cpf.pipe';
import { DetalheFeriasHomologadas, ConsultaGerencialService, DetalhesAguardandoHomologacaoResponse, DetalhesFeriasHomologadasResponse, DetalhesExercicioSemProgramacaoResponse, FiltroTotalizadoresConsultaGerencial, TotalizadoresConsultaGerencial } from 'src/app/services/consulta-gerencial.service';
import { VinculosService } from 'src/app/services/vinculos.service';
import { EnumPerfil } from 'src/app/components/shared/enums/perfil.enum';
import { EnumTransacao } from 'src/app/components/shared/enums/transacao.enum';
import { EnumNivelAcesso } from 'src/app/components/shared/enums/nivel-acesso.enum';
import { SituacaoExercicioFeriasEnum } from 'src/app/components/shared/enums/situacao-exercicio-ferias.enum';
import { Message } from 'src/app/components/shared/message';
import { formatarMensagem } from 'src/app/components/shared/string-utils';

@Component({
    selector: 'app-consulta-gerencial',
    templateUrl: './consulta-gerencial.component.html',
    styleUrls: ['./consulta-gerencial.component.scss']
})
export class ConsultaGerencialComponent extends FeriasBaseComponent implements OnInit {
    private readonly chaveEstadoTelaConsultaGerencial = 'consultaGerencialTelaState';
    private readonly chaveAtualizarConsultaAposAcao = 'consultaGerencialAtualizarAposAcao';
    readonly hintExercicio = 'Selecione o exercício das férias para consultar os status. Serão exibidos o ano anterior, o ano atual e o ano seguinte.';
    readonly hintAguardandoHomologacao = 'Férias solicitadas pelo agente público, mas ainda pendentes de homologação pela chefia.';
    readonly hintExercicioSemProgramacao = 'Férias do exercício selecionado ainda não programadas.';
    readonly hintFeriasHomologadas = 'Férias já programadas e homologadas para o exercício selecionado.';
    formFiltro: FormGroup;
    submetido = false;
    totalizadores: TotalizadoresConsultaGerencial | null = null;
    opcoesExercicio: { id: number; label: string }[] = [];
    detalhesAguardandoHomologacao: DetalhesAguardandoHomologacaoResponse | null = null;
    detalhesExercicioSemProgramacao: DetalhesExercicioSemProgramacaoResponse | null = null;
    detalhesFeriasHomologadas: DetalhesFeriasHomologadasResponse | null = null;
    carregandoDetalhesAguardandoHomologacao = false;
    carregandoDetalhesExercicioSemProgramacao = false;
    carregandoDetalhesFeriasHomologadas = false;
    exportandoCsvAguardandoHomologacao = false;
    exportandoCsvExercicioSemProgramacao = false;
    exportandoCsvFeriasHomologadas = false;
    menuAguardandoSelecionado: MenuItem[] = [];
    menuSemProgramacaoSelecionado: MenuItem[] = [];
    menuFeriasHomologadasSelecionado: MenuItem[] = [];
    readonly tamanhoPaginaDetalhes = 10;
    selecaoUorgObrigatoria = false;
    uorgUnicaBloqueada = false;
    private filtroDetalhesAtual: FiltroTotalizadoresConsultaGerencial | null = null;
    private tipoDetalheAtual: 'aguardandoHomologacao' | 'exercicioSemProgramacao' | 'feriasHomologadas' | null = null;
    private dadosUsuarioLogado: SigepeUserData | null = null;
    private filtrosPadraoUsuario = {
        codigoOrgao: null as number | null,
        codigoUpag: null as number | null,
        codigoUorg: null as number | null
    };
    private readonly cachePendenciaProgramarAnterior = new Map<string, { valor: boolean; timestamp: number }>();
    private readonly ttlCachePendenciaProgramarAnteriorMs = 60_000;

    constructor(
        private fb: FormBuilder,
        private consultaGerencialService: ConsultaGerencialService,
        private vinculosService: VinculosService,
        private segurancaService: SegurancaService,
        private cpfPipe: CpfPipe,
        private router: Router,
        alertaMsgService: AlertMessageService
    ) {
        super(alertaMsgService);

        afterNextRender(() => {
            this.alterarTituloTemplate('Consulta Gerencial');
            this.alterarTextoBreadcrumb(1, 'Consulta Gerencial');
        });
    }

    ngOnInit(): void {
        this.clearMessages();
        this.criarOpcoesExercicio();
        this.initForm();
        this.restaurarEstadoTela();
        this.atualizarConsultaAposAcaoSeNecessario();
        this.segurancaService.userData$.subscribe(dadosUsuarioLogado => {
            this.dadosUsuarioLogado = dadosUsuarioLogado;
            this.selecaoUorgObrigatoria = `${dadosUsuarioLogado?.nivelAcesso?.id ?? ''}` === EnumNivelAcesso.UORG_SUBORDINADAS.id;
            const restricoes = NivelAcessoUtil.obterRestricaoCombos(dadosUsuarioLogado);
            this.filtrosPadraoUsuario = {
                codigoOrgao: restricoes.orgaoPreSelecionado,
                codigoUpag: restricoes.upagPreSelecionado,
                codigoUorg: restricoes.uorgPreSelecionado
            };
            this.preSelecionarUorgPrincipalConsultaGerencial(dadosUsuarioLogado);
        });
    }

    criarOpcoesExercicio(): void {
        const anoAtual = new Date().getFullYear();
        this.opcoesExercicio = [
            { id: anoAtual - 1, label: `${anoAtual - 1} (Ano anterior)` },
            { id: anoAtual, label: `${anoAtual} (Ano atual)` },
            { id: anoAtual + 1, label: `${anoAtual + 1} (Ano seguinte)` }
        ];
    }

    initForm(): void {
        const anoAtual = new Date().getFullYear();
        this.formFiltro = this.fb.group({
            codigoOrgao: [null],
            codigoUpag: [null],
            codigoUorg: [null],
            cpf: [''],
            nome: [''],
            matricula: [''],
            exercicio: [anoAtual]
        });
        this.formFiltro.controls.exercicio.valueChanges.subscribe(valor => {
            if (!valor) {
                this.formFiltro.controls.exercicio.setValue(anoAtual, {
                    emitEvent: false
                });
            }
        });
    }

    onChangeOrgao(): void {
        this.formFiltro.controls.codigoUpag.setValue(null);
        this.formFiltro.controls.codigoUorg.setValue(null);
        this.uorgUnicaBloqueada = false;
    }

    onChangeUpag(): void {
        this.formFiltro.controls.codigoUorg.setValue(null);
        this.uorgUnicaBloqueada = false;
    }

    onOpcoesUorgCarregadas(evento: { quantidade: number; bloqueado: boolean }): void {
        this.uorgUnicaBloqueada = !!evento?.bloqueado;
    }

    private preSelecionarUorgPrincipalConsultaGerencial(dadosUsuarioLogado: SigepeUserData | null): void {
        const ehUorgSubordinadas = `${dadosUsuarioLogado?.nivelAcesso?.id ?? ''}` === EnumNivelAcesso.UORG_SUBORDINADAS.id;
        const codigoUorgPrincipal = dadosUsuarioLogado?.codigoUorgSiape;
        if (!ehUorgSubordinadas || !codigoUorgPrincipal || isNaN(codigoUorgPrincipal)) {
            return;
        }

        const selecionarUorg = () => {
            if (this.formFiltro && this.formFiltro.controls['codigoUpag'].value && !this.formFiltro.controls['codigoUorg'].value) {
                this.formFiltro.controls['codigoUorg'].setValue(Number(codigoUorgPrincipal));
            } else if (this.formFiltro && !this.formFiltro.controls['codigoUpag'].value) {
                setTimeout(() => {
                    selecionarUorg();
                }, 100);
            }
        };
        selecionarUorg();
    }

    private montarFiltroConsulta(): FiltroTotalizadoresConsultaGerencial {
        const codigoOrgao = Number(this.formFiltro.controls.codigoOrgao.value);
        const codigoUpagValor = this.formFiltro.controls.codigoUpag.value;
        const codigoUpag = codigoUpagValor ? Number(codigoUpagValor) : null;
        const codigoUorgValor = this.formFiltro.controls.codigoUorg.value;
        const codigoUorg = codigoUorgValor ? Number(codigoUorgValor) : null;
        const cpfValor = this.formFiltro.controls.cpf.value;
        const cpf = cpfValor ? Number(`${cpfValor}`.replace(/\D/g, '')) : null;
        const nomeValor = this.formFiltro.controls.nome.value;
        const nome = nomeValor ? `${nomeValor}`.trim() : null;
        const matriculaValor = this.formFiltro.controls.matricula.value;
        const matricula = matriculaValor ? Number(matriculaValor) : null;
        const exercicioValor = this.formFiltro.controls.exercicio.value;
        const exercicio = exercicioValor ? Number(exercicioValor) : null;

        return {
            nivel: 1,
            orgao: codigoOrgao,
            upag: codigoUpag,
            uorg: codigoUorg,
            exercicio,
            cpf,
            nome,
            matricula
        };
    }

    pesquisar(): void {
        this.submetido = true;
        this.getAlertaMsgService().dismissAll();

        if (!this.validarFormularioConsulta()) {
            return;
        }

        const filtro = this.montarFiltroConsulta();
        const inicioMedicao = this.iniciarMedicaoCarregamento('totalizadores', filtro);

        this.consultaGerencialService.obterTotalizadores(filtro).subscribe({
            next: (resultado: TotalizadoresConsultaGerencial) => {
                this.totalizadores = {
                    aguardandoHomologacao: resultado?.aguardandoHomologacao ?? 0,
                    exercicioSemProgramacao: resultado?.exercicioSemProgramacao ?? 0,
                    feriasHomologadas: resultado?.feriasHomologadas ?? 0
                };
                this.detalhesAguardandoHomologacao = null;
                this.detalhesExercicioSemProgramacao = null;
                this.detalhesFeriasHomologadas = null;
                this.tipoDetalheAtual = null;
                this.finalizarMedicaoCarregamento('totalizadores', inicioMedicao, 'sucesso', {
                    totalizadores: this.totalizadores
                });
            },
            error: erro => {
                this.finalizarMedicaoCarregamento('totalizadores', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    private validarCamposObrigatoriosDetalhes(): boolean {
        if (this.formFiltro.controls.cpf.errors?.['invalidCpf']) {
            return false;
        }
        if (this.selecaoUorgObrigatoria && !this.uorgUnicaBloqueada && !this.formFiltro.controls.codigoUorg.value) {
            this.getAlertaMsgService().error('O campo UORG é obrigatório.');
            return false;
        }
        if (!this.formFiltro.controls.codigoOrgao.value) {
            this.getAlertaMsgService().error('O campo Órgão é obrigatório.');
            return false;
        }
        if (!this.formFiltro.controls.exercicio.value) {
            this.getAlertaMsgService().error('O campo Exercício das férias é obrigatório para detalhar.');
            return false;
        }
        return true;
    }

    onMatriculaInput(): void {
        const valorAtual = `${this.formFiltro.controls.matricula.value ?? ''}`;
        const valorNormalizado = valorAtual.replace(/\D/g, '').slice(0, 7);

        if (valorAtual !== valorNormalizado) {
            this.formFiltro.controls.matricula.setValue(valorNormalizado, {
                emitEvent: false
            });
        }
    }

    abrirDetalhesAguardandoHomologacao(event: Event): void {
        event.preventDefault();

        if (!this.validarCamposObrigatoriosDetalhes()) {
            return;
        }

        this.filtroDetalhesAtual = this.montarFiltroConsulta();
        this.tipoDetalheAtual = 'aguardandoHomologacao';
        this.detalhesExercicioSemProgramacao = null;
        this.detalhesFeriasHomologadas = null;
        this.carregarDetalhesAguardandoHomologacao(0);
    }

    abrirDetalhesExercicioSemProgramacao(event: Event): void {
        event.preventDefault();

        if (!this.validarCamposObrigatoriosDetalhes()) {
            return;
        }

        this.filtroDetalhesAtual = this.montarFiltroConsulta();
        this.tipoDetalheAtual = 'exercicioSemProgramacao';
        this.detalhesAguardandoHomologacao = null;
        this.detalhesFeriasHomologadas = null;
        this.carregarDetalhesExercicioSemProgramacao(0);
    }

    abrirDetalhesFeriasHomologadas(event: Event): void {
        event.preventDefault();

        if (!this.validarCamposObrigatoriosDetalhes()) {
            return;
        }

        this.filtroDetalhesAtual = this.montarFiltroConsulta();
        this.tipoDetalheAtual = 'feriasHomologadas';
        this.detalhesAguardandoHomologacao = null;
        this.detalhesExercicioSemProgramacao = null;
        this.carregarDetalhesFeriasHomologadas(0);
    }

    onMudancaPaginaDetalhes(event: { page?: number; first?: number; rows?: number }): void {
        const pagina = typeof event?.page === 'number' ? event.page : typeof event?.first === 'number' && typeof event?.rows === 'number' && event.rows > 0 ? Math.floor(event.first / event.rows) : 0;
        if (this.tipoDetalheAtual === 'aguardandoHomologacao') {
            this.carregarDetalhesAguardandoHomologacao(pagina);
            return;
        }
        if (this.tipoDetalheAtual === 'feriasHomologadas') {
            this.carregarDetalhesFeriasHomologadas(pagina);
            return;
        }
        this.carregarDetalhesExercicioSemProgramacao(pagina);
    }

    exportarCsvAguardandoHomologacao(): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }
        const inicioMedicao = this.iniciarMedicaoCarregamento('exportacao-csv-aguardando-homologacao', this.filtroDetalhesAtual);
        this.exportandoCsvAguardandoHomologacao = true;
        this.consultaGerencialService.exportarCsvAguardandoHomologacao(this.filtroDetalhesAtual).subscribe({
            next: arquivo => {
                this.baixarArquivoCsv(arquivo, 'aguardando-homologacao');
                this.finalizarMedicaoCarregamento('exportacao-csv-aguardando-homologacao', inicioMedicao, 'sucesso', {
                    tamanhoBytes: arquivo?.size ?? 0
                });
            },
            error: () => {
                this.exportandoCsvAguardandoHomologacao = false;
                this.finalizarMedicaoCarregamento('exportacao-csv-aguardando-homologacao', inicioMedicao, 'erro');
                this.getAlertaMsgService().error('Não foi possível exportar o CSV de Aguardando homologação.');
            },
            complete: () => {
                this.exportandoCsvAguardandoHomologacao = false;
            }
        });
    }

    exportarCsvExercicioSemProgramacao(): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }
        const inicioMedicao = this.iniciarMedicaoCarregamento('exportacao-csv-exercicio-sem-programacao', this.filtroDetalhesAtual);
        this.exportandoCsvExercicioSemProgramacao = true;
        this.consultaGerencialService.exportarCsvExercicioSemProgramacao(this.filtroDetalhesAtual).subscribe({
            next: arquivo => {
                this.baixarArquivoCsv(arquivo, 'exercicio-sem-programacao');
                this.finalizarMedicaoCarregamento('exportacao-csv-exercicio-sem-programacao', inicioMedicao, 'sucesso', {
                    tamanhoBytes: arquivo?.size ?? 0
                });
            },
            error: () => {
                this.exportandoCsvExercicioSemProgramacao = false;
                this.finalizarMedicaoCarregamento('exportacao-csv-exercicio-sem-programacao', inicioMedicao, 'erro');
                this.getAlertaMsgService().error('Não foi possível exportar o CSV de Exercício sem programação.');
            },
            complete: () => {
                this.exportandoCsvExercicioSemProgramacao = false;
            }
        });
    }

    exportarCsvFeriasHomologadas(): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }
        const inicioMedicao = this.iniciarMedicaoCarregamento('exportacao-csv-ferias-homologadas', this.filtroDetalhesAtual);
        this.exportandoCsvFeriasHomologadas = true;
        this.consultaGerencialService.exportarCsvFeriasHomologadas(this.filtroDetalhesAtual).subscribe({
            next: arquivo => {
                this.baixarArquivoCsv(arquivo, 'ferias-homologadas');
                this.finalizarMedicaoCarregamento('exportacao-csv-ferias-homologadas', inicioMedicao, 'sucesso', {
                    tamanhoBytes: arquivo?.size ?? 0
                });
            },
            error: () => {
                this.exportandoCsvFeriasHomologadas = false;
                this.finalizarMedicaoCarregamento('exportacao-csv-ferias-homologadas', inicioMedicao, 'erro');
                this.getAlertaMsgService().error('Não foi possível exportar o CSV de Férias homologadas.');
            },
            complete: () => {
                this.exportandoCsvFeriasHomologadas = false;
            }
        });
    }

    get exibirAcaoHomologar(): boolean {
        return this.possuiTransacao(EnumTransacao.FERIAS_HOMOLOGAR);
    }

    homologarAguardandoHomologacao(registro: any): void {
        this.navegarParaTransacao('/secure/consulta-gerencial/homologar-ferias', registro);
    }

    detalharAguardandoHomologacao(registro: any): void {
        this.navegarParaTransacao('/secure/consulta-gerencial/detalhar-ferias', registro);
    }

    private obterChaveCachePendenciaProgramarAnterior(codigoOrgao: number, matricula: number, anoExercicio: number): string {
        return `${codigoOrgao}|${matricula}|${anoExercicio}`;
    }

    private obterResultadoCachePendenciaProgramarAnterior(codigoOrgao: number, matricula: number, anoExercicio: number): boolean | null {
        const chave = this.obterChaveCachePendenciaProgramarAnterior(codigoOrgao, matricula, anoExercicio);
        const cache = this.cachePendenciaProgramarAnterior.get(chave);

        if (!cache) {
            return null;
        }

        const expirou = Date.now() - cache.timestamp > this.ttlCachePendenciaProgramarAnteriorMs;

        if (expirou) {
            this.cachePendenciaProgramarAnterior.delete(chave);
            return null;
        }

        return cache.valor;
    }

    private salvarResultadoCachePendenciaProgramarAnterior(codigoOrgao: number, matricula: number, anoExercicio: number, possuiPendencia: boolean): void {
        const chave = this.obterChaveCachePendenciaProgramarAnterior(codigoOrgao, matricula, anoExercicio);
        this.cachePendenciaProgramarAnterior.set(chave, {
            valor: possuiPendencia,
            timestamp: Date.now()
        });
    }

    private bloquearProgramarPorPendenciaExercicioAnterior(anoAnterior: number, iniciouMedicao: ReturnType<ConsultaGerencialComponent['iniciarMedicaoCarregamento']>): void {
        this.getAlertaMsgService().error(
            formatarMensagem(Message.AVISO_FERIAS_ANTERIORES_PENDENTES_HOMOLOGACAO, {
                ano: String(anoAnterior)
            })
        );
        this.finalizarMedicaoCarregamento('validacao-programar-exercicio-anterior', iniciouMedicao, 'sucesso', {
            bloqueadoPorPendencia: true,
            anoAnterior
        });
    }

    programarExercicioSemProgramacao(registro: any): void {
        console.log('[ConsultaGerencial] programarExercicioSemProgramacao - registro', registro);

        const codigoOrgao = Number(registro?.codigoOrgao ?? 0);
        const matricula = Number(registro?.matricula ?? 0);
        const anoExercicio = Number(registro?.exercicioFerias ?? 0);
        const inicioMedicao = this.iniciarMedicaoCarregamento('validacao-programar-exercicio-anterior', {
            codigoOrgao,
            matricula,
            anoExercicio
        });

        if (!codigoOrgao || !matricula || !anoExercicio) {
            this.finalizarMedicaoCarregamento('validacao-programar-exercicio-anterior', inicioMedicao, 'sucesso', {
                validacaoIgnorada: true
            });
            this.navegarParaTransacao('/secure/consulta-gerencial/programar-ferias', registro);
            return;
        }

        const cacheValido = this.obterResultadoCachePendenciaProgramarAnterior(codigoOrgao, matricula, anoExercicio);

        if (cacheValido !== null) {
            if (cacheValido) {
                const anoAnterior = anoExercicio - 1;
                this.bloquearProgramarPorPendenciaExercicioAnterior(anoAnterior, inicioMedicao);
                return;
            }

            this.finalizarMedicaoCarregamento('validacao-programar-exercicio-anterior', inicioMedicao, 'sucesso', {
                bloqueadoPorPendencia: false,
                anoAnterior: anoExercicio - 1
            });
            this.navegarParaTransacao('/secure/consulta-gerencial/programar-ferias', registro);
            return;
        }

        const anoAnterior = anoExercicio - 1;
        this.vinculosService.obterVinculoAgentePublico({ codigoOrgao, matricula }).subscribe({
            next: resultado => {
                const exercicioAnterior = resultado?.exerciciosFerias?.find(exercicio => Number(exercicio?.ano) === anoAnterior);
                const possuiPendenciaProgramacaoAnterior = exercicioAnterior?.situacao === SituacaoExercicioFeriasEnum.NAO_SOLICITADA.descricao || exercicioAnterior?.situacao === SituacaoExercicioFeriasEnum.SOLICITADA.descricao;

                this.salvarResultadoCachePendenciaProgramarAnterior(codigoOrgao, matricula, anoExercicio, possuiPendenciaProgramacaoAnterior);

                if (possuiPendenciaProgramacaoAnterior) {
                    this.bloquearProgramarPorPendenciaExercicioAnterior(anoAnterior, inicioMedicao);
                    return;
                }

                this.finalizarMedicaoCarregamento('validacao-programar-exercicio-anterior', inicioMedicao, 'sucesso', {
                    bloqueadoPorPendencia: false,
                    anoAnterior
                });
                this.navegarParaTransacao('/secure/consulta-gerencial/programar-ferias', registro);
            },
            error: erro => {
                this.finalizarMedicaoCarregamento('validacao-programar-exercicio-anterior', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    detalharExercicioSemProgramacao(registro: any): void {
        this.navegarParaTransacao('/secure/consulta-gerencial/detalhar-ferias', registro);
    }

    detalharFeriasHomologadas(registro: DetalheFeriasHomologadas): void {
        this.navegarParaTransacao('/secure/consulta-gerencial/detalhar-ferias', registro);
    }

    obterAcoesAguardandoHomologacao(registro: any): MenuItem[] {
        const acoes: MenuItem[] = [];

        if (this.possuiTransacao(EnumTransacao.FERIAS_HOMOLOGAR) && this.podeExecutarAcoesNoRegistro(registro)) {
            acoes.push({
                label: 'Homologar',
                command: () => this.homologarAguardandoHomologacao(registro)
            });
        }

        if (this.possuiTransacao(EnumTransacao.FERIAS_DETALHAR)) {
            acoes.push({
                label: 'Detalhar',
                command: () => this.detalharAguardandoHomologacao(registro)
            });
        }

        return acoes;
    }

    obterAcoesExercicioSemProgramacao(registro: any): MenuItem[] {
        const acoes: MenuItem[] = [];

        if (this.possuiTransacao(EnumTransacao.FERIAS_PROGRAMAR) && this.podeExecutarAcoesNoRegistro(registro)) {
            acoes.push({
                label: 'Programar',
                command: () => this.programarExercicioSemProgramacao(registro)
            });
        }

        return acoes;
    }

    obterAcoesFeriasHomologadas(registro: any): MenuItem[] {
        const acoes: MenuItem[] = [];

        if (this.possuiTransacao(EnumTransacao.FERIAS_DETALHAR)) {
            acoes.push({
                label: 'Detalhar',
                command: () => this.detalharFeriasHomologadas(registro)
            });
        }

        return acoes;
    }

    abrirMenuAguardandoHomologacao(event: Event, menuAguardando: any, registro: any): void {
        event.preventDefault();
        event.stopPropagation();
        this.menuAguardandoSelecionado = this.obterAcoesAguardandoHomologacao(registro);
        menuAguardando.toggle(event);
    }

    abrirMenuExercicioSemProgramacao(event: Event, menuSemProgramacao: any, registro: any): void {
        event.preventDefault();
        event.stopPropagation();
        this.menuSemProgramacaoSelecionado = this.obterAcoesExercicioSemProgramacao(registro);
        menuSemProgramacao.toggle(event);
    }

    abrirMenuFeriasHomologadas(event: Event, menuFeriasHomologadas: any, registro: any): void {
        event.preventDefault();
        event.stopPropagation();
        this.menuFeriasHomologadasSelecionado = this.obterAcoesFeriasHomologadas(registro);
        menuFeriasHomologadas.toggle(event);
    }

    private navegarParaTransacao(url: string, registro: any): void {
        const estado = this.montarEstadoExercicioSelecionado(registro);
        console.log('[ConsultaGerencial] navegarParaTransacao', {
            url,
            estado,
            registro
        });
        this.salvarEstadoTela();
        sessionStorage.setItem('consultaGerencialExercicioSelecionado', JSON.stringify(estado));
        this.router.navigate([url], { state: { data: estado } });
    }

    private salvarEstadoTela(): void {
        const estadoTela = {
            formFiltro: this.formFiltro?.getRawValue?.() ?? null,
            submetido: this.submetido,
            totalizadores: this.totalizadores,
            detalhesAguardandoHomologacao: this.detalhesAguardandoHomologacao,
            detalhesExercicioSemProgramacao: this.detalhesExercicioSemProgramacao,
            detalhesFeriasHomologadas: this.detalhesFeriasHomologadas,
            filtroDetalhesAtual: this.filtroDetalhesAtual,
            tipoDetalheAtual: this.tipoDetalheAtual
        };
        sessionStorage.setItem(this.chaveEstadoTelaConsultaGerencial, JSON.stringify(estadoTela));
    }

    private restaurarEstadoTela(): void {
        try {
            const estadoSerializado = sessionStorage.getItem(this.chaveEstadoTelaConsultaGerencial);
            if (!estadoSerializado) {
                return;
            }
            const estadoTela = JSON.parse(estadoSerializado);
            if (estadoTela?.formFiltro && this.formFiltro) {
                this.formFiltro.patchValue(estadoTela.formFiltro);
            }
            this.submetido = !!estadoTela?.submetido;
            this.totalizadores = estadoTela?.totalizadores ?? null;
            this.detalhesAguardandoHomologacao = estadoTela?.detalhesAguardandoHomologacao ?? null;
            this.detalhesExercicioSemProgramacao = estadoTela?.detalhesExercicioSemProgramacao ?? null;
            this.detalhesFeriasHomologadas = estadoTela?.detalhesFeriasHomologadas ?? null;
            this.filtroDetalhesAtual = estadoTela?.filtroDetalhesAtual ?? null;
            this.tipoDetalheAtual = estadoTela?.tipoDetalheAtual ?? null;
        } catch {
            // Se houver estado inválido, apenas ignora.
        } finally {
            sessionStorage.removeItem(this.chaveEstadoTelaConsultaGerencial);
        }
    }

    private montarEstadoExercicioSelecionado(registro: any): any {
        const parcelas = (registro?.parcelas || []).map(parcela => this.mapearParcelaParaDetalhamento(parcela));
        const nomeAgentePublico = registro?.nomeServidor ?? this.extrairNomeAgentePublico(registro);

        return {
            // Campos exigidos pelo fluxo legado em ConsultarExerciciosComponent
            codigoOrgao: registro?.codigoOrgao ?? null,
            matricula: registro?.matricula ?? null,
            situacaoVinculo: true,
            origemTela: 'consulta-gerencial',

            idFerias: registro?.idFerias ?? null,
            cpf: this.cpfPipe.transform(registro?.cpf ?? ''),
            nome: nomeAgentePublico,
            chefiaImediata: this.montarDescricaoComHifen(registro?.nomeChefiaUnidadeExercicio, registro?.codigoUorgChefiaUnidadeExercicio ?? registro?.codigoUorgChefiaImediata),
            vinculoEmExercicio: registro?.matricula != null ? `${registro.matricula} - ${registro?.descricaoCargo ?? 'SEM CARGO NO ÓRGÃO'}` : '',
            orgaoEmExercicio: (registro?.codigoOrgao != null ? `${registro.codigoOrgao}` : '') + (registro?.siglaOrgaoExercicio ? ` - ${registro.siglaOrgaoExercicio}` : '') + (registro?.codigoOrgao != null || registro?.siglaOrgaoExercicio || registro?.descricaoOrgaoExercicio ? ' - ' : '') + (registro?.descricaoOrgaoExercicio ?? ''),
            codigoSituacaoFuncional: registro?.codigoSituacaoFuncional ?? null,
            regime: registro?.siglaRegimeJuridico ?? '',
            siglaRegime: registro?.siglaRegimeJuridico ?? '',
            anoExercicio: registro?.exercicioFerias ?? null,
            saldoDias: registro?.saldoDias ?? 0,
            situacaoExercicio: this.mapearSituacaoExercicioParaDescricao(registro?.situacaoExercicio) ?? 'Homologada',
            dataInicioPeriodoAquisitivo: registro?.dataInicioPeriodoAquisitivo ?? '',
            dataFimPeriodoAquisitivo: registro?.dataFimPeriodoAquisitivo ?? '',
            dataInicioPeriodoProgramacao: registro?.dataInicioPeriodoProgramacao ?? '',
            dataFimPeriodoProgramacao: registro?.dataFimPeriodoProgramacao ?? '',
            ...(registro?.tipoFeriasEspecial ? { tipoFeriasEspecial: registro.tipoFeriasEspecial } : {}),
            ...(registro?.descricaoTipoFeriasEspecial
                ? {
                      descricaoTipoFeriasEspecial: registro.descricaoTipoFeriasEspecial
                  }
                : {}),
            dataInicioPeriodoProgramacaoDisponivel: undefined,
            dataFimPeriodoProgramacaoDisponivel: undefined,
            parcelas,
            raioX: false,
            parametrosFeriasOrgao: {
                pagamentoNoAdiantamento: false,
                quantidadeParcelasPagamento: 3,
                percentualAdiantamento: 0
            },
            parametrosFeriasCargo: {
                grupoCargo: '',
                quantidadeDiasFerias: registro?.saldoDias ?? 30,
                permiteAbonoPecuniario: true
            },
            justificativaPerdaDireito: '',
            periodoAquisitivo: registro?.periodoAquisitivo ?? ''
        };
    }

    private mapearParcelaParaDetalhamento(parcela: any): any {
        const numeroParcela = `${parcela?.numeroParcela ?? parcela?.ordem ?? ''}`;
        const [ordemTexto, geracaoTexto] = numeroParcela.split('.');

        return {
            id: parcela?.id ?? null,
            idParcelaPai: parcela?.idParcelaPai ?? null,
            ordem: parcela?.ordem ?? (ordemTexto ? Number(ordemTexto) : null),
            geracao: geracaoTexto ? Number(geracaoTexto) : (parcela?.geracao ?? 0),
            dataInicio: this.normalizarDataDetalhamentoParcela(parcela?.dataInicio ?? parcela?.inicio ?? null),
            dataFim: this.normalizarDataDetalhamentoParcela(parcela?.dataFim ?? parcela?.fim ?? null),
            quantidadeDias: parcela?.quantidadeDias ?? parcela?.dias ?? 0,
            adiantamentoSalarial: parcela?.adiantamentoSalarial ?? false,
            abonoPecuniario: parcela?.abonoPecuniario ?? false,
            adiantamentoDecimoTerceiro: parcela?.adiantamentoDecimoTerceiro ?? false,
            parcelasDesconto: parcela?.parcelasDesconto ?? null,
            situacao: this.mapearSituacaoParcelaParaDescricao(parcela?.situacao),
            justificativaAcumulo: parcela?.justificativaAcumulo ?? null,
            dataInterrupcao: parcela?.dataInterrupcao ?? null,
            motivoLegal: parcela?.motivoLegal ?? null,
            justificativaInterrupcao: parcela?.justificativaInterrupcao ?? null,
            documentoLegal: parcela?.documentoLegal ?? null,
            dataPublicacaoDocumentoLegal: parcela?.dataPublicacaoDocumentoLegal ?? null
        };
    }

    private normalizarDataDetalhamentoParcela(valor: string | null | undefined): string | null | undefined {
        if (!valor || typeof valor !== 'string') {
            return valor;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
            const [dia, mes, ano] = valor.split('/');
            return `${ano}-${mes}-${dia}`;
        }

        return valor;
    }

    private mapearSituacaoParcelaParaDescricao(situacao: string | null | undefined): string | null {
        if (!situacao) {
            return null;
        }
        const mapa: Record<string, string> = {
            SOL: 'Solicitada',
            INT: 'Interrompida',
            NSL: 'Não Solicitada',
            HOM: 'Homologada',
            EGZ: 'Em Gozo',
            IND: 'Indenizada',
            NIN: 'Não Indenizada',
            USU: 'Usufruída',
            ENC: 'Encerrada',
            PDR: 'Perda de Direito',
            CAN: 'Cancelada'
        };
        return mapa[situacao] ?? situacao;
    }

    private extrairNomeAgentePublico(registro: any): string {
        const matriculaNomeAgentePublico = `${registro?.matriculaNomeServidor ?? ''}`;
        const separador = ' - ';

        if (!matriculaNomeAgentePublico.includes(separador)) {
            return '';
        }

        return matriculaNomeAgentePublico.split(separador).slice(1).join(separador);
    }

    private montarDescricaoComHifen(primeiraParte?: string | number | null, segundaParte?: string | number | null): string {
        return [primeiraParte, segundaParte]
            .map(parte => `${parte ?? ''}`.trim())
            .filter(parte => !!parte)
            .join(' - ');
    }

    private validarFormularioConsulta(): boolean {
        if (this.formFiltro.controls.cpf.errors?.['invalidCpf']) {
            return false;
        }
        if (this.selecaoUorgObrigatoria && !this.uorgUnicaBloqueada && !this.formFiltro.controls.codigoUorg.value) {
            this.getAlertaMsgService().error('O campo UORG é obrigatório.');
            return false;
        }

        if (!this.formFiltro.controls.codigoOrgao.value) {
            this.getAlertaMsgService().error('O campo Órgão é obrigatório.');
            return false;
        }

        if (!this.formFiltro.controls.exercicio.value) {
            this.getAlertaMsgService().error('O campo Exercício das férias é obrigatório.');
            return false;
        }

        return true;
    }

    private atualizarConsultaAposAcaoSeNecessario(): void {
        const deveAtualizar = sessionStorage.getItem(this.chaveAtualizarConsultaAposAcao) === 'true';
        sessionStorage.removeItem(this.chaveAtualizarConsultaAposAcao);

        if (!deveAtualizar) {
            return;
        }
        if (!this.validarFormularioConsulta()) {
            return;
        }

        const filtro = this.montarFiltroConsulta();
        this.filtroDetalhesAtual = filtro;
        const inicioMedicao = this.iniciarMedicaoCarregamento('totalizadores-apos-acao', filtro);

        this.consultaGerencialService.obterTotalizadores(filtro).subscribe({
            next: (resultado: TotalizadoresConsultaGerencial) => {
                this.totalizadores = {
                    aguardandoHomologacao: resultado?.aguardandoHomologacao ?? 0,
                    exercicioSemProgramacao: resultado?.exercicioSemProgramacao ?? 0,
                    feriasHomologadas: resultado?.feriasHomologadas ?? 0
                };
                this.finalizarMedicaoCarregamento('totalizadores-apos-acao', inicioMedicao, 'sucesso', {
                    totalizadores: this.totalizadores
                });
                this.atualizarDetalhamentoAposAcao();
            },
            error: erro => {
                this.finalizarMedicaoCarregamento('totalizadores-apos-acao', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    private atualizarDetalhamentoAposAcao(): void {
        if (!this.filtroDetalhesAtual || !this.tipoDetalheAtual) {
            return;
        }
        if (this.tipoDetalheAtual === 'aguardandoHomologacao') {
            this.carregarDetalhesAguardandoHomologacao(0);
            return;
        }
        if (this.tipoDetalheAtual === 'feriasHomologadas') {
            this.carregarDetalhesFeriasHomologadas(0);
            return;
        }
        this.carregarDetalhesExercicioSemProgramacao(0);
    }

    private mapearSituacaoExercicioParaDescricao(situacao: string | null | undefined): string | null {
        if (!situacao) {
            return null;
        }
        const mapa: Record<string, string> = {
            SOL: 'Solicitada',
            NSL: 'Não Solicitada',
            HOM: 'Homologada',
            ENC: 'Encerrada',
            TMP: 'Temporária'
        };
        return mapa[situacao] ?? situacao;
    }

    private possuiTransacao(transacao: EnumTransacao): boolean {
        if (!this.dadosUsuarioLogado?.transacoes?.length) {
            return false;
        }
        return this.dadosUsuarioLogado.transacoes.some(t => t.toLowerCase() === transacao.descricao.toLowerCase());
    }

    // Conforme regra da tabela de permissões:
    // FERIAS_OPERACIONAL não pode executar ações de manutenção (programar, homologar)
    // nas próprias férias. As demais restrições são geridas pela transação.
    private podeExecutarAcoesNoRegistro(registro: any): boolean {
        const cpfRegistro = registro?.cpf ? `${registro.cpf}`.replace(/\D/g, '') : null;

        if (!cpfRegistro) {
            return true;
        }

        const cpfUsuario = this.dadosUsuarioLogado?.sub?.replace(/\D/g, '');

        if (cpfUsuario && this.possuiPerfil(EnumPerfil.FERIAS_OPERACIONAL) && cpfUsuario === cpfRegistro) {
            return false;
        }

        return true;
    }

    private possuiPerfil(perfil: EnumPerfil): boolean {
        if (!this.dadosUsuarioLogado?.perfis?.length) {
            return false;
        }

        return this.dadosUsuarioLogado.perfis.some(perfilUsuario => perfilUsuario?.toLowerCase() === perfil.descricao.toLowerCase());
    }

    private iniciarMedicaoCarregamento(operacao: string, filtro: any): number {
        const inicio = performance.now();
        console.log(`[ConsultaGerencial][Performance] ${operacao} - INICIO`, {
            inicioMs: inicio,
            filtro,
            semExercicio: !filtro?.exercicio
        });
        return inicio;
    }

    private finalizarMedicaoCarregamento(operacao: string, inicio: number, status: 'sucesso' | 'erro', dados?: any): void {
        const fim = performance.now();
        const duracaoMs = Math.round((fim - inicio) * 100) / 100;
        console.log(`[ConsultaGerencial][Performance] ${operacao} - FIM`, {
            status,
            duracaoMs,
            inicioMs: inicio,
            fimMs: fim,
            dados
        });
    }

    private carregarDetalhesAguardandoHomologacao(numeroPagina: number): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }

        const filtro = {
            ...this.filtroDetalhesAtual,
            numeroPagina,
            tamanhoPagina: this.tamanhoPaginaDetalhes
        };
        const inicioMedicao = this.iniciarMedicaoCarregamento('detalhes-aguardando-homologacao', filtro);

        this.carregandoDetalhesAguardandoHomologacao = true;
        this.consultaGerencialService.obterDetalhesAguardandoHomologacao(filtro).subscribe({
            next: resultado => {
                this.detalhesAguardandoHomologacao = resultado;
                this.carregandoDetalhesAguardandoHomologacao = false;
                this.finalizarMedicaoCarregamento('detalhes-aguardando-homologacao', inicioMedicao, 'sucesso', {
                    totalRegistros: resultado?.totalRegistros ?? 0,
                    numeroPagina: resultado?.numeroPagina ?? 0
                });
            },
            error: erro => {
                this.carregandoDetalhesAguardandoHomologacao = false;
                this.finalizarMedicaoCarregamento('detalhes-aguardando-homologacao', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    private carregarDetalhesExercicioSemProgramacao(numeroPagina: number): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }

        const filtro = {
            ...this.filtroDetalhesAtual,
            numeroPagina,
            tamanhoPagina: this.tamanhoPaginaDetalhes
        };
        const inicioMedicao = this.iniciarMedicaoCarregamento('detalhes-exercicio-sem-programacao', filtro);

        this.carregandoDetalhesExercicioSemProgramacao = true;
        this.consultaGerencialService.obterDetalhesExercicioSemProgramacao(filtro).subscribe({
            next: resultado => {
                this.detalhesExercicioSemProgramacao = resultado;
                this.carregandoDetalhesExercicioSemProgramacao = false;
                this.finalizarMedicaoCarregamento('detalhes-exercicio-sem-programacao', inicioMedicao, 'sucesso', {
                    totalRegistros: resultado?.totalRegistros ?? 0,
                    numeroPagina: resultado?.numeroPagina ?? 0
                });
            },
            error: erro => {
                this.carregandoDetalhesExercicioSemProgramacao = false;
                this.finalizarMedicaoCarregamento('detalhes-exercicio-sem-programacao', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    private carregarDetalhesFeriasHomologadas(numeroPagina: number): void {
        if (!this.filtroDetalhesAtual) {
            return;
        }

        const filtro = {
            ...this.filtroDetalhesAtual,
            numeroPagina,
            tamanhoPagina: this.tamanhoPaginaDetalhes
        };
        const inicioMedicao = this.iniciarMedicaoCarregamento('detalhes-ferias-homologadas', filtro);

        this.carregandoDetalhesFeriasHomologadas = true;
        this.consultaGerencialService.obterDetalhesFeriasHomologadas(filtro).subscribe({
            next: resultado => {
                this.detalhesFeriasHomologadas = resultado;
                this.carregandoDetalhesFeriasHomologadas = false;
                this.finalizarMedicaoCarregamento('detalhes-ferias-homologadas', inicioMedicao, 'sucesso', {
                    totalRegistros: resultado?.totalRegistros ?? 0,
                    numeroPagina: resultado?.numeroPagina ?? 0
                });
            },
            error: erro => {
                this.carregandoDetalhesFeriasHomologadas = false;
                this.finalizarMedicaoCarregamento('detalhes-ferias-homologadas', inicioMedicao, 'erro', {
                    erro
                });
            }
        });
    }

    limpar(): void {
        const anoAtual = new Date().getFullYear();
        this.getAlertaMsgService().dismissAll();
        const restricoes = NivelAcessoUtil.obterRestricaoCombos(this.dadosUsuarioLogado);
        this.formFiltro.reset({
            codigoOrgao: restricoes.orgaoDesabilitado ? this.formFiltro.controls.codigoOrgao.value : null,
            codigoUpag: restricoes.upagDesabilitado ? this.formFiltro.controls.codigoUpag.value : null,
            codigoUorg: restricoes.uorgDesabilitado ? this.formFiltro.controls.codigoUorg.value : null,
            cpf: '',
            nome: '',
            matricula: '',
            exercicio: anoAtual
        });
        this.totalizadores = null;
        this.detalhesAguardandoHomologacao = null;
        this.detalhesExercicioSemProgramacao = null;
        this.detalhesFeriasHomologadas = null;
        this.filtroDetalhesAtual = null;
        this.carregandoDetalhesAguardandoHomologacao = false;
        this.carregandoDetalhesExercicioSemProgramacao = false;
        this.carregandoDetalhesFeriasHomologadas = false;
        this.exportandoCsvAguardandoHomologacao = false;
        this.exportandoCsvExercicioSemProgramacao = false;
        this.exportandoCsvFeriasHomologadas = false;
        this.tipoDetalheAtual = null;
        this.submetido = false;
        sessionStorage.removeItem(this.chaveEstadoTelaConsultaGerencial);
    }

    private baixarArquivoCsv(blob: Blob, sufixo: string): void {
        const dataHora = new Date();
        const pad = (valor: number) => String(valor).padStart(2, '0');
        const nomeArquivo = `consulta-gerencial-${sufixo}-${dataHora.getFullYear()}${pad(dataHora.getMonth() + 1)}${pad(dataHora.getDate())}-${pad(dataHora.getHours())}${pad(dataHora.getMinutes())}${pad(dataHora.getSeconds())}.csv`;
        const arquivo = new Blob([blob], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(arquivo);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
